/**
 * SponsorsSection - Combined sponsors section with flagship and regular sponsors
 * Extracted from EventsTab.jsx
 */
import { Plus, Trash2, Users, Trophy, X, ExternalLink } from "lucide-react";
import { Button } from "../../ui/button";

// Flagship sponsor subsection
const FlagshipSponsorSubsection = ({ formData, setFormData }) => (
  <div className="p-4 bg-amber-500/5 border border-amber-500/20 mb-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Trophy className="w-5 h-5 text-amber-500" />
        <div>
          <h5 className="font-secondary text-xs font-bold uppercase text-amber-500">Flagship Sponsor</h5>
          <p className="text-[10px] text-zinc-500">Display a prominent sponsor on the event page header</p>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input 
          type="checkbox" 
          checked={formData.flagship_sponsor_enabled}
          onChange={(e) => setFormData({...formData, flagship_sponsor_enabled: e.target.checked})}
          className="w-4 h-4"
        />
        <span className="text-xs text-zinc-400">Enable</span>
      </label>
    </div>
    
    {formData.flagship_sponsor_enabled && (
      <div className="space-y-3 pt-3 border-t border-amber-500/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor Name</label>
            <input 
              type="text"
              value={formData.flagship_sponsor_name}
              onChange={(e) => setFormData({...formData, flagship_sponsor_name: e.target.value})}
              placeholder="e.g., Nike"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor Logo URL</label>
            <input 
              type="text"
              value={formData.flagship_sponsor_logo_url}
              onChange={(e) => setFormData({...formData, flagship_sponsor_logo_url: e.target.value})}
              placeholder="https://..."
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor Website URL</label>
            <input 
              type="text"
              value={formData.flagship_sponsor_url}
              onChange={(e) => setFormData({...formData, flagship_sponsor_url: e.target.value})}
              placeholder="https://sponsor-website.com"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            />
          </div>
        </div>
        {formData.flagship_sponsor_logo_url && (
          <div className="flex items-center gap-4 p-3 bg-zinc-900/50 border border-amber-500/20 rounded">
            <img src={formData.flagship_sponsor_logo_url} alt="Flagship Sponsor" className="h-12 max-w-[150px] object-contain" />
            <div className="text-xs text-zinc-500">
              <span className="text-amber-500 font-bold">Presented by</span> {formData.flagship_sponsor_name || "Sponsor"}
              {formData.flagship_sponsor_url && (
                <a href={formData.flagship_sponsor_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300">
                  <ExternalLink className="w-3 h-3 inline" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

// Add sponsor form with file uploads
const AddSponsorForm = ({ newSponsor, setNewSponsor, onAdd }) => (
  <div className="space-y-3 mb-4 p-4 bg-zinc-800/30 border border-white/5">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <input 
        type="text" 
        value={newSponsor.name} 
        onChange={(e) => setNewSponsor({...newSponsor, name: e.target.value})}
        placeholder="Sponsor Name *"
        className="bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
      />
      <div></div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Logo Upload/URL */}
      <div className="space-y-2">
        <label className="block text-xs text-zinc-500 uppercase">Logo (URL or Upload)</label>
        <input 
          type="text" 
          value={newSponsor.logo_url} 
          onChange={(e) => setNewSponsor({...newSponsor, logo_url: e.target.value, logo_file: ''})}
          placeholder="Logo URL"
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        />
        <div className="text-center text-xs text-zinc-600">- or -</div>
        <input 
          type="file" 
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              const reader = new FileReader();
              reader.onloadend = () => {
                setNewSponsor({...newSponsor, logo_file: reader.result, logo_url: ''});
              };
              reader.readAsDataURL(file);
            }
          }}
          className="w-full text-xs text-zinc-500 file:mr-2 file:py-1 file:px-3 file:border-0 file:bg-zinc-700 file:text-white file:text-xs"
        />
        {(newSponsor.logo_url || newSponsor.logo_file) && (
          <img src={newSponsor.logo_file || newSponsor.logo_url} alt="Logo preview" className="h-12 object-contain" />
        )}
      </div>
      
      {/* Banner Upload/URL */}
      <div className="space-y-2">
        <label className="block text-xs text-zinc-500 uppercase">Banner (URL or Upload) - Optional</label>
        <input 
          type="text" 
          value={newSponsor.banner_url} 
          onChange={(e) => setNewSponsor({...newSponsor, banner_url: e.target.value, banner_file: ''})}
          placeholder="Banner URL"
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        />
        <div className="text-center text-xs text-zinc-600">- or -</div>
        <input 
          type="file" 
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              const reader = new FileReader();
              reader.onloadend = () => {
                setNewSponsor({...newSponsor, banner_file: reader.result, banner_url: ''});
              };
              reader.readAsDataURL(file);
            }
          }}
          className="w-full text-xs text-zinc-500 file:mr-2 file:py-1 file:px-3 file:border-0 file:bg-zinc-700 file:text-white file:text-xs"
        />
        {(newSponsor.banner_url || newSponsor.banner_file) && (
          <img src={newSponsor.banner_file || newSponsor.banner_url} alt="Banner preview" className="h-12 object-contain" />
        )}
      </div>
    </div>
    
    <Button 
      type="button" 
      onClick={onAdd}
      disabled={!newSponsor.name.trim()}
      className="bg-zinc-700 text-white hover:bg-zinc-600 text-xs px-4 py-2 h-auto rounded-none disabled:opacity-50"
    >
      <Plus className="w-4 h-4 mr-1" /> Add Sponsor
    </Button>
  </div>
);

// Sponsors list display
const SponsorsList = ({ sponsors, onRemove }) => (
  sponsors.length > 0 && (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {sponsors.map((sponsor, idx) => (
        <div key={sponsor.id || idx} className="flex items-center gap-2 p-3 bg-zinc-800/50 border border-white/10">
          {(sponsor.logo_file || sponsor.logo_url) && (
            <img src={sponsor.logo_file || sponsor.logo_url} alt={sponsor.name} className="h-10 w-10 object-contain" />
          )}
          <div className="flex-1 min-w-0">
            <span className="text-xs text-white block truncate">{sponsor.name}</span>
            {(sponsor.banner_file || sponsor.banner_url) && (
              <span className="text-[10px] text-zinc-500">+ Banner</span>
            )}
          </div>
          <button 
            type="button" 
            onClick={() => onRemove(idx)}
            className="text-zinc-500 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  )
);

// Main sponsors section
export const SponsorsSection = ({ formData, setFormData, newSponsor, setNewSponsor }) => {
  const addSponsor = () => {
    if (!newSponsor.name) return;
    setFormData({
      ...formData,
      sponsors: [...formData.sponsors, { ...newSponsor, id: Date.now().toString() }]
    });
    setNewSponsor({ name: "", logo_url: "", banner_url: "", logo_file: "", banner_file: "" });
  };

  const removeSponsor = (index) => {
    setFormData({
      ...formData,
      sponsors: formData.sponsors.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="border-t border-white/10 pt-6">
      <h4 className="font-secondary text-sm font-bold uppercase text-white mb-4 flex items-center gap-2">
        <Users className="w-4 h-4" /> Event Sponsors
      </h4>
      
      <FlagshipSponsorSubsection formData={formData} setFormData={setFormData} />
      <AddSponsorForm newSponsor={newSponsor} setNewSponsor={setNewSponsor} onAdd={addSponsor} />
      <SponsorsList sponsors={formData.sponsors} onRemove={removeSponsor} />
    </div>
  );
};

export default SponsorsSection;
